/* eslint-disable react/prop-types */
import * as React from 'react';

import { DatePicker } from 'antd';
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';

import { cn } from 'utils';

type CommonPickerProps = Pick<
  DatePickerProps | RangePickerProps,
  'showNow' | 'needConfirm' | 'format' | 'className'
>;
type DateConverter = {
  convertDates: (dates: string | string[]) => Array<string | undefined>;
};
type DatePickerConstraints = {
  onDateChange?: (dates: Array<string | undefined>) => void;
};

const useDatePicker = ({
  showNow = true,
  needConfirm = true,
  format = 'YYYY/MM/DD, HH:mm:ss',
  className,
  ...props
}: CommonPickerProps): CommonPickerProps & DateConverter => {
  const convertDates = React.useCallback<DateConverter['convertDates']>(
    dates =>
      (Array.isArray(dates) ? dates : [dates]).map(date => {
        if (!date) return undefined;

        const jalaliDate = dayjs(date, { jalali: true });
        return jalaliDate.calendar('gregory').toISOString();
      }),
    [],
  );

  return {
    showNow,
    needConfirm,
    format,
    className: cn(
      'w-full [&_.ant-picker-input>input]:[direction:ltr] [&_.ant-picker-input>input]:text-right',
      className,
    ),
    convertDates,
    ...props,
  };
};

type AntdDatePickerProps = React.ComponentPropsWithoutRef<typeof DatePicker> &
  DatePickerConstraints;

const AntdDatePicker = React.forwardRef<
  React.ElementRef<typeof DatePicker>,
  AntdDatePickerProps
>(
  (
    {
      showTime = true,
      showNow,
      needConfirm,
      format,
      className,
      onDateChange,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { convertDates, ...datePicker } = useDatePicker({
      showNow,
      needConfirm,
      format,
      className,
    });

    return (
      <DatePicker
        ref={ref}
        showTime={showTime}
        onChange={(...params) => {
          const [, dateString] = params;
          const convertedDates = convertDates(dateString);

          onChange?.(...params);
          onDateChange?.(convertedDates);
        }}
        {...datePicker}
        {...props}
      />
    );
  },
);
AntdDatePicker.displayName = 'AntdDatePicker';

type AntdDateRangePickerProps = React.ComponentPropsWithoutRef<
  typeof DatePicker.RangePicker
> &
  DatePickerConstraints;

const AntdDateRangePicker = React.forwardRef<
  React.ElementRef<typeof DatePicker.RangePicker>,
  AntdDateRangePickerProps
>(
  (
    {
      showTime = true,
      showNow,
      needConfirm,
      format,
      className,
      onDateChange,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { convertDates, ...datePicker } = useDatePicker({
      showNow,
      needConfirm,
      format,
      className,
    });

    return (
      <DatePicker.RangePicker
        ref={ref}
        showTime={showTime}
        onChange={(...params) => {
          const [, dateStrings] = params;
          const convertedDates = convertDates(dateStrings);

          onChange?.(...params);
          onDateChange?.(convertedDates);
        }}
        {...datePicker}
        {...props}
      />
    );
  },
);
AntdDateRangePicker.displayName = 'AntdDateRangePicker';

export { AntdDatePicker, AntdDateRangePicker };
export { useDatePicker };
